/**
 * Reports: the pages a project's data is published as and their secret links.
 *
 * A report is created, configured and rebound by staff; anyone who may see the project's contents
 * reads it. The secret link needs no session at all.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

export type Report = components['schemas']['Report'];
export type ReportKind = components['schemas']['ReportKindEnum'];
export type ReportInput = components['schemas']['ReportInput'];
export type PublicReport = components['schemas']['PublicReport'];
/** Where an acquisition's camera positions come from, as its preprocess run decided them. */
export type PhotoPositioning = components['schemas']['PhotoPositioning'];
export type CreateReportBody = components['schemas']['CreateReportRequest'];

export type ReportKindInfo = components['schemas']['ReportKindInfo'];
export type ReportSlot = components['schemas']['ReportSlot'];

/** The kind catalogue, from `viewer/report_kinds.py` through the API. */
export async function listReportKinds(): Promise<ReportKindInfo[]> {
  return unwrap(await api.GET('/api/report-kinds'));
}
export function useReportKinds() {
  return useQuery({ queryKey: ['report-kinds'], queryFn: listReportKinds, staleTime: Infinity });
}

const P = (id: string) => ({ params: { path: { public_id: id } } });

export async function listProjectReports(projectId: string): Promise<Report[]> {
  return unwrap(await api.GET('/api/projects/{public_id}/reports', P(projectId)));
}
export async function createReport(projectId: string, body: CreateReportBody): Promise<Report> {
  return unwrap(await api.POST('/api/projects/{public_id}/reports', { ...P(projectId), body }));
}
export async function getReport(id: string): Promise<Report> {
  return unwrap(await api.GET('/api/reports/{public_id}', P(id)));
}
export interface ReportPatch {
  name?: string;
  options?: Record<string, unknown>;
  visible_to_client?: boolean;
}
export async function patchReport(id: string, patch: ReportPatch): Promise<Report> {
  return unwrap(await api.PATCH('/api/reports/{public_id}', { ...P(id), body: patch }));
}
export async function deleteReport(id: string): Promise<void> {
  unwrap(await api.DELETE('/api/reports/{public_id}', P(id)));
}
/** `object: null` unbinds an optional slot (RP8). */
export async function rebindReport(id: string, role: string, object: string | null): Promise<Report> {
  return unwrap(await api.POST('/api/reports/{public_id}/rebind', { ...P(id), body: { role, object } }));
}
export async function rotateReportSecret(id: string): Promise<Report> {
  return unwrap(await api.POST('/api/reports/{public_id}/rotate-secret', P(id)));
}
/** The secret link's own payload: the page, its settings and its inputs — nothing else (N13). */
export async function publicReport(secret: string): Promise<PublicReport> {
  return unwrap(await api.GET('/api/r/{secret}', { params: { path: { secret } } }));
}

export function useProjectReports(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['reports', projectId],
    queryFn: () => listProjectReports(projectId!),
    enabled: !!projectId && enabled,
  });
}
export function useReport(id: string | undefined) {
  return useQuery({ queryKey: ['report', id], queryFn: () => getReport(id!), enabled: !!id });
}
export function usePublicReport(secret: string | undefined) {
  return useQuery({ queryKey: ['public-report', secret], queryFn: () => publicReport(secret!), enabled: !!secret, retry: false });
}

function useInvalidateReports() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['reports'] });
    if (id) void qc.invalidateQueries({ queryKey: ['report', id] });
  };
}

export function useCreateReport(projectId: string) {
  const invalidate = useInvalidateReports();
  return useMutation({ mutationFn: (body: CreateReportBody) => createReport(projectId, body), onSuccess: () => invalidate() });
}
export function usePatchReport(id: string) {
  const invalidate = useInvalidateReports();
  return useMutation({
    mutationFn: (patch: ReportPatch) => patchReport(id, patch),
    onSuccess: () => invalidate(id),
  });
}
export function useRebindReport(id: string) {
  const invalidate = useInvalidateReports();
  return useMutation({
    mutationFn: ({ role, object }: { role: string; object: string | null }) => rebindReport(id, role, object),
    onSuccess: () => invalidate(id),
  });
}
export function useDeleteReport() {
  const invalidate = useInvalidateReports();
  return useMutation({ mutationFn: (id: string) => deleteReport(id), onSuccess: () => invalidate() });
}
export function useRotateReportSecret() {
  const invalidate = useInvalidateReports();
  return useMutation({ mutationFn: (id: string) => rotateReportSecret(id), onSuccess: (r) => invalidate(r.public_id) });
}
